import { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, ShieldCheck, Activity, Database, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import RecentActivityWidget from '@/components/RecentActivityWidget';
import ComplianceAlerts from '@/components/ComplianceAlerts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/analytics/admin`, { withCredentials: true });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">A carregar...</div>;
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Painel de Administração
        </h1>
        <p className="text-slate-600">
          Bem-vindo, <span className="font-semibold">{user?.name}</span>. Gestão completa do sistema.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/users')}>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-slate-600">Utilizadores</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-[#0F172A]">{analytics?.users?.total || 0}</div>
                <div className="text-sm text-slate-600 mt-1">{analytics?.users?.active || 0} ativos</div>
              </div>
              <Users className="w-8 h-8 text-[#3B82F6]" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/consents')}>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-slate-600">Conformidade</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-[#10B981]">{analytics?.compliance_score || 0}%</div>
                <div className="text-sm text-slate-600 mt-1">Score geral</div>
              </div>
              <ShieldCheck className="w-8 h-8 text-[#10B981]" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/data-breaches')}>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-slate-600">Violações de Dados</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-[#EF4444]">{analytics?.breaches?.total || 0}</div>
                <div className="text-sm text-slate-600 mt-1">{analytics?.breaches?.critical || 0} críticas</div>
              </div>
              <AlertTriangle className="w-8 h-8 text-[#EF4444]" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/backups')}>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-slate-600">Backups</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-lg font-bold text-[#0F172A]">Sistema Ativo</div>
                <div className="text-sm text-slate-600 mt-1">Automático diário</div>
              </div>
              <Database className="w-8 h-8 text-[#0F172A]" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle>Ações Rápidas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              data-testid="manage-users-button"
              onClick={() => navigate('/admin/users')}
              className="w-full justify-start bg-[#3B82F6] hover:bg-[#3B82F6]/90"
            >
              <Users className="w-4 h-4 mr-2" />
              Gerir Utilizadores
            </Button>
            <Button
              data-testid="view-audit-logs-button"
              onClick={() => navigate('/admin/audit-logs')}
              className="w-full justify-start bg-[#0F172A] hover:bg-[#0F172A]/90"
            >
              <Activity className="w-4 h-4 mr-2" />
              Ver Logs de Auditoria
            </Button>
            <Button
              data-testid="manage-backups-button"
              onClick={() => navigate('/admin/backups')}
              className="w-full justify-start bg-[#10B981] hover:bg-[#10B981]/90"
            >
              <Database className="w-4 h-4 mr-2" />
              Gerir Backups
            </Button>
          </CardContent>
        </Card>

        <RecentActivityWidget limit={10} />
      </div>

      {/* Compliance Alerts Section */}
      <ComplianceAlerts compact={false} />

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>Estatísticas de Conformidade</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div 
              className="text-center cursor-pointer p-4 rounded-lg hover:bg-slate-50 transition-colors"
              onClick={() => navigate('/consents')}
              data-testid="active-consents-stat"
            >
              <div className="text-4xl font-bold text-[#10B981] mb-2">
                {analytics?.consents?.active_rate || 0}%
              </div>
              <p className="text-sm text-slate-600">Taxa de Consentimentos Ativos</p>
              <p className="text-xs text-blue-500 mt-1">Ver consentimentos →</p>
            </div>
            <div 
              className="text-center cursor-pointer p-4 rounded-lg hover:bg-slate-50 transition-colors"
              onClick={() => navigate('/marketing-actions')}
              data-testid="approval-rate-stat"
            >
              <div className="text-4xl font-bold text-[#3B82F6] mb-2">
                {analytics?.marketing_actions?.approval_rate || 0}%
              </div>
              <p className="text-sm text-slate-600">Taxa de Aprovação de Ações</p>
              <p className="text-xs text-blue-500 mt-1">Ver ações de marketing →</p>
            </div>
            <div 
              className="text-center cursor-pointer p-4 rounded-lg hover:bg-slate-50 transition-colors"
              onClick={() => navigate('/analytics')}
              data-testid="compliance-score-stat"
            >
              <div className="text-4xl font-bold text-[#10B981] mb-2">
                {analytics?.compliance_score || 0}%
              </div>
              <p className="text-sm text-slate-600">Score de Conformidade Global</p>
              <p className="text-xs text-blue-500 mt-1">Ver analytics →</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
